import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useEntities } from '~/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { useCompetitorsDrawer } from '~/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/useCompetitorsDrawer';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { CompetitorCard } from '~/widgets/theme-widget/tabs/theme-exposure-tab/components/competitor-card/CompetitorCard';

import * as S from './CompetitorsSection.styles';

/**
 * @deprecated
 */
export const CompetitorsSection = ({
  entityTag,
  isInDrawer = false,
}: {
  entityTag: string;
  isInDrawer?: boolean;
}) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const { openDrawer } = useCompetitorsDrawer();

  const {
    data = [],
    isError,
    isFetching,
    error,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit: 3,
  });
  const { navigateToPage } = useNavigateToAssetOverview();

  const entityTags = data.map(item => item.company);

  const {
    data: entitiesData = [],
    isError: isEntitiesError,
    isLoading: isEntitiesLoading,
  } = useEntities({
    entityTags,
    enabled: !!entityTags.length,
  });

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.Competitors,
  }).toString();

  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const navigateToAssetOverview = (entityTag: string) => {
    navigateToPage({ entityTag });
  };

  const getContent = () => {
    if (isFetching || isEntitiesLoading) {
      return <S.LoadingSkeleton data-testid="loading-skeleton" />;
    } else if (isError || isEntitiesError || !entitiesData.length) {
      return null;
    } else {
      return (
        <S.CompetitorsWrapper data-testid="competitors-wrapper">
          {entitiesData.map((entity, index) => {
            const entityRank = index + 1;
            return (
              <CompetitorCard
                entity={entity}
                key={entity.tag}
                rank={entityRank}
                clickHandler={() =>
                  isInDrawer
                    ? navigateToAssetOverview(entity.tag)
                    : openDrawer(entity.tag)
                }
              />
            );
          })}
        </S.CompetitorsWrapper>
      );
    }
  };

  if ((!isFetching && !isError && !entitiesData.length) || error) {
    return null;
  }

  return (
    <S.SectionRow>
      <SectionTitle
        title={t(
          'assetOverview.knowledgeGraph.competitors.tabs.competitorDetail.competitorsSectionTitle'
        )}
        variant="small"
        linkTo={linkToKnowledgeGraph}
      />
      {getContent()}
    </S.SectionRow>
  );
};
